import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { B1, Frame } from '../../../assests/ExportImageIcon'
// B2/B3/SampleUser were only used by the commented-out static `blogs` mock below.
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { showToast } from '../../../componants/toast/toastService'
import SaveToWishlistButton from '../../../componants/wishlist/SaveToWishlistButton'
import useIndividualWishlistItems, { wishlistItemKey } from '../../../componants/wishlist/useIndividualWishlistItems'
import './eventList.css'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''

// Static fallback — kept for reference. Real events come from
// GET /customer_events/ below (filtered by ?category=/?search=/?artist= from the URL).
// const blogs = [
//     { img: B1, title: 'Top 10 Hill Stations to Visit This Summer', author: 'finzo', date: '21 April, 2026' },
//     { img: B2, title: 'A Traveler\'s Guide to Booking Cheap Flights', author: 'finzo', date: '18 April, 2026' },
//     { img: B3, title: 'Best Beaches in Goa You Shouldn\'t Miss', author: 'finzo', date: '15 April, 2026' },
//     { img: B1, title: '5 Tips to Plan a Budget-Friendly Trip', author: 'finzo', date: '10 April, 2026' },
//     { img: B2, title: 'Why Rishikesh is the Perfect Adventure Getaway', author: 'finzo', date: '05 April, 2026' },
//     { img: B3, title: 'How to Choose the Right Travel Insurance', author: 'finzo', date: '01 April, 2026' },
// ]

const formatEventDateTime = (value) => {
    if (!value) return ''
    const date = new Date(value.replace(' ', 'T'))
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatEventPrice = (value) => {
    if (value === null || value === undefined || value === '') return null
    const num = Number(value)
    return Number.isNaN(num) ? null : `₹${num.toLocaleString('en-IN')}`
}

const EARTH_RADIUS_KM = 6371
const toRad = (deg) => (deg * Math.PI) / 180

/** Haversine distance in km between two lat/lng points. */
const distanceKm = (lat1, lng1, lat2, lng2) => {
    const dLat = toRad(lat2 - lat1)
    const dLng = toRad(lng2 - lng1)
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const EventList = ({ heading = 'Our Latest Insights', showFilters = false, paginated = false }) => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const category = searchParams.get('category') || ''
    const search = searchParams.get('search') || ''
    const artist = searchParams.get('artist') || ''
    const [events, setEvents] = useState([])
    const [nextPageUrl, setNextPageUrl] = useState(null)
    const [loadingMore, setLoadingMore] = useState(false)

    const [categories, setCategories] = useState([])
    const [filterPanelOpen, setFilterPanelOpen] = useState(false)
    const [searchDraft, setSearchDraft] = useState(search)
    const [artistDraft, setArtistDraft] = useState(artist)
    const [sortByPriceAsc, setSortByPriceAsc] = useState(false)
    const [nearbyFilterActive, setNearbyFilterActive] = useState(false)
    const [userLocation, setUserLocation] = useState(null)
    const [locatingUser, setLocatingUser] = useState(false)
    const { individualId, savedMap } = useIndividualWishlistItems()

    useEffect(() => {
        if (!showFilters) return
        service
            .get(endpoints.customerEvents.categories, {}, { silent: true })
            .then(({ data }) => setCategories(data.data || []))
            .catch(() => {})
    }, [showFilters])

    useEffect(() => {
        setSearchDraft(search)
    }, [search])

    useEffect(() => {
        setArtistDraft(artist)
    }, [artist])

    // Live search — debounced so it doesn't fire an API call on every keystroke.
    useEffect(() => {
        if (!showFilters) return
        const trimmed = searchDraft.trim()
        if (trimmed === search) return
        const timer = setTimeout(() => {
            const next = new URLSearchParams(searchParams)
            if (trimmed) next.set('search', trimmed)
            else next.delete('search')
            setSearchParams(next)
        }, 400)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDraft, showFilters])

    // Same debounced-live pattern for the separate artist filter.
    useEffect(() => {
        if (!showFilters) return
        const trimmed = artistDraft.trim()
        if (trimmed === artist) return
        const timer = setTimeout(() => {
            const next = new URLSearchParams(searchParams)
            if (trimmed) next.set('artist', trimmed)
            else next.delete('artist')
            setSearchParams(next)
        }, 400)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artistDraft, showFilters])

    useEffect(() => {
        const params = {}
        if (category) params.category = category
        if (search) params.search = search
        if (artist) params.artist = artist
        service
            .get(endpoints.customerEvents.list, params, { silent: true })
            .then(({ data }) => {
                setEvents(data.data || [])
                setNextPageUrl(paginated ? (data.next || null) : null)
            })
            .catch(() => {})
    }, [category, search, artist, paginated])

    const handleLoadMore = () => {
        if (!nextPageUrl || loadingMore) return
        setLoadingMore(true)
        const relativeUrl = nextPageUrl.replace(API_BASE_URL, '')
        service
            .get(relativeUrl, {}, { silent: true })
            .then(({ data }) => {
                setEvents((prev) => [...prev, ...(data.data || [])])
                setNextPageUrl(data.next || null)
            })
            .catch(() => {})
            .finally(() => setLoadingMore(false))
    }

    // /customer_events/ only supports `category` and `search` as real filters — everything
    // else in the design (sort, duration, distance) has no backing field/param on the API.
    const applyCategory = (categoryId) => {
        const next = new URLSearchParams(searchParams)
        if (categoryId) next.set('category', categoryId)
        else next.delete('category')
        setSearchParams(next)
    }

    const applySearch = () => {
        const next = new URLSearchParams(searchParams)
        if (searchDraft.trim()) next.set('search', searchDraft.trim())
        else next.delete('search')
        if (artistDraft.trim()) next.set('artist', artistDraft.trim())
        else next.delete('artist')
        setSearchParams(next)
        setFilterPanelOpen(false)
    }

    const clearFilters = () => {
        setSearchDraft('')
        setArtistDraft('')
        setSearchParams({})
        setFilterPanelOpen(false)
    }

    const hasActiveFilters = Boolean(category || search || artist)

    const toggleNearbyFilter = () => {
        if (nearbyFilterActive) {
            setNearbyFilterActive(false)
            return
        }
        if (!navigator.geolocation) {
            showToast('Location is not supported on this device/browser.', 'error')
            return
        }
        setLocatingUser(true)
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude })
                setNearbyFilterActive(true)
                setLocatingUser(false)
            },
            () => {
                showToast('Could not access your location. Please allow location access and try again.', 'error')
                setLocatingUser(false)
            },
            { enableHighAccuracy: false, timeout: 10000 }
        )
    }

    // Client-side only — the API has no `ordering`/`sort`/distance param, so this can only
    // sort/filter whatever page(s) of events are already loaded.
    const displayedEvents = useMemo(() => {
        let list = events
        if (nearbyFilterActive && userLocation) {
            list = list.filter((event) => {
                if (event.venue_latitude == null || event.venue_longitude == null) return false
                return distanceKm(userLocation.lat, userLocation.lng, Number(event.venue_latitude), Number(event.venue_longitude)) <= 10
            })
        }
        if (sortByPriceAsc) {
            list = [...list].sort((a, b) => {
                const priceA = a.start_price == null ? Infinity : Number(a.start_price)
                const priceB = b.start_price == null ? Infinity : Number(b.start_price)
                return priceA - priceB
            })
        }
        return list
    }, [events, sortByPriceAsc, nearbyFilterActive, userLocation])

    return (
        <>
            <section className='ourLatestBlog popularDestination paddingCmn'>
                <div className='container'>
                    <div className='row'>
                        <div className='col-md-12'>
                            <div className='secHeadArea'>
                                <div className='mainHead'>{heading}</div>
                                {!paginated && <Link to="/event-list">View All</Link>}
                            </div>

                            {showFilters && (
                                <div className='eventFilterBarWrap'>
                                    <div className="eventFilterBar">
                                        <button
                                            type="button"
                                            className={`eventFilterButton ${hasActiveFilters ? 'active' : ''}`}
                                            onClick={() => setFilterPanelOpen((prev) => !prev)}
                                        >
                                            <img src={Frame} alt="Filters" /> Filters{hasActiveFilters ? ' •' : ''}
                                        </button>

                                        <button
                                            type="button"
                                            className={`eventFilterButton ${sortByPriceAsc ? 'active' : ''}`}
                                            onClick={() => setSortByPriceAsc((prev) => !prev)}
                                        >
                                            Price: Low to High
                                        </button>

                                        {/* No popularity/likes field on /customer_events/ — can't sort by "Popular" honestly.
                                        <button type="button" className="eventFilterButton">
                                            Sort: Popular
                                        </button>
                                        */}

                                        {/* No duration field on the event list/detail API. */}
                                        {/* <button type="button" className="eventFilterButton">
                                            Duration
                                        </button> */}

                                        <button
                                            type="button"
                                            className={`eventFilterButton ${nearbyFilterActive ? 'active' : ''}`}
                                            onClick={toggleNearbyFilter}
                                            disabled={locatingUser}
                                        >
                                            {locatingUser ? 'Locating…' : 'Under 10 KM'}
                                        </button>

                                        {/* Not a distinct backend filter — "Music" is just one of the real
                                            categories, already selectable from the Filters panel below. */}
                                        {/* <button type="button" className="eventFilterButton">
                                            Music
                                        </button> */}

                                        {hasActiveFilters && (
                                            <button type="button" className="eventFilterButton" onClick={clearFilters}>
                                                Clear
                                            </button>
                                        )}
                                    </div>

                                    {filterPanelOpen && (
                                        <div className='eventFilterPanel'>
                                            <div className='eventFilterPanel__section'>
                                                <label>Search by event name</label>
                                                <div className='eventFilterPanel__searchRow'>
                                                    <input
                                                        type='text'
                                                        placeholder='Search events'
                                                        value={searchDraft}
                                                        onChange={(e) => setSearchDraft(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                                                    />
                                                    <button type='button' className='commonBtn' onClick={applySearch}>Search</button>
                                                </div>
                                            </div>

                                            <div className='eventFilterPanel__section'>
                                                <label>Filter by artist name</label>
                                                <div className='eventFilterPanel__searchRow'>
                                                    <input
                                                        type='text'
                                                        placeholder='Search by artist'
                                                        value={artistDraft}
                                                        onChange={(e) => setArtistDraft(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                                                    />
                                                    <button type='button' className='commonBtn' onClick={applySearch}>Search</button>
                                                </div>
                                            </div>

                                            <div className='eventFilterPanel__section'>
                                                <label>Category</label>
                                                <div className='eventFilterPanel__chips'>
                                                    {categories.map((cat) => (
                                                        <button
                                                            type='button'
                                                            key={cat.id}
                                                            className={`eventFilterChip ${String(category) === String(cat.id) ? 'active' : ''}`}
                                                            onClick={() => applyCategory(String(category) === String(cat.id) ? '' : cat.id)}
                                                        >
                                                            {cat.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}


                        </div>
                            {displayedEvents.map((event) => (
                                <div className='col-12 col-md-3 col-lg-4' key={event.id}>
                                    <div className='slidePart h-100 hoverLift' onClick={() => navigate(`/event-detail/${event.id}`)}>
                                        <img src={event.cover_image || B1} alt={event.name} className='eventMainImage' />
                                        <SaveToWishlistButton
                                            className='hotelCard__wishlist eventWishlist'
                                            item={{
                                                recordModule: 'event',
                                                recordId: event.id,
                                                recordName: event.name,
                                                imageUrl: event.cover_image,
                                            }}
                                            preSavedItemId={savedMap.get(wishlistItemKey('event', event.id))}
                                            preSavedWishlistId={individualId}
                                        />
                                        <div className='blogAuthor'>{event.venue_name} | {formatEventDateTime(event.start_date_time)}</div>
                                        <div className='blogTitle'>{event.name}</div>
                                        <div className='blogAuthor'>
                                            {event.artist_name}
                                            {formatEventPrice(event.start_price) ? ` | From ${formatEventPrice(event.start_price)}` : ''}
                                        </div>
                                    </div>
                                </div>

                            ))}
                            {paginated && !displayedEvents.length && (
                                <p className='fare-extra-hint'>No events found.</p>
                            )}
                        {paginated && nextPageUrl && (
                            <div className='col-md-12 text-center mt-4'>
                                <button type='button' className='travellerAddBtn' onClick={handleLoadMore} disabled={loadingMore}>
                                    {loadingMore ? 'Loading…' : 'Load More'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    )
}

export default EventList;
